<template>
  <div class="scratchpad-container">
    <div class="flex-gap-2">
      <div class="history-scroll-area scrollbar">
        <h3 class="history-title">History</h3>
        <button @click="handleNewPageClick" class="btn btn-default-add" type="button">
          New page
        </button>
        <ul class="history-list" data-test="scratchpad-history-list">
          <li
            v-for="(entry, index) in chatHistory"
            :key="'' + entry.pageId + index"
            class="history-list-item"
            :class="{
              'selected-chat': currentChatEntry && entry.pageId === currentChatEntry.pageId,
            }"
            @click="handleSelectEntry(entry)"
            @keydown.enter="handleSelectEntry(entry)"
          >
            <a
              href="javascript:void(0)"
              class="history-list-item-link"
              data-test="scratchpad-history-item"
              >{{ getHistoryItemTitle(entry) }}</a
            >
            <span
              v-show="currentChatEntry && entry.pageId === currentChatEntry.pageId"
              class="glyphicon glyphicon-trash"
              @click="deleteEntry(entry)"
              @keydown.enter="deleteEntry(entry)"
            ></span>
          </li>
        </ul>
      </div>
      <div class="flex-item">
        <p v-if="deleteError" class="text-danger">{{ deleteError }}</p>
        <ScratchpadPage
          v-if="currentChatEntry"
          :currentChat="currentChatEntry"
          @update-history-item="updateHistoryItem"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import ScratchpadPage from "../components/smart-scratchpad/smart-scratchpad.vue";
import {
  createNewChatEntry,
  deleteChatEntry,
  getAllScratchpadEntries,
} from "../components/smart-scratchpad/scratchpad-history";
import { ScratchpadEntryApiModel } from "../api-models";
import { csContext } from "../services/cs-context";

const chatHistory = ref<ScratchpadEntryApiModel[]>([]);
const currentChatEntry = ref<ScratchpadEntryApiModel | null>(null);
const deleteError = ref<string | null>(null);

const route = useRoute();

function getHistoryItemTitle(entry: ScratchpadEntryApiModel): string {
  if (!entry.pageTitle) {
    if (!entry.text) {
      return "Untitled";
    }
    return `${entry.text.substring(0, 20)} ...`;
  }

  if (entry.pageTitle.length > 20) {
    return `${entry.pageTitle.substring(0, 20)} ...`;
  }

  return entry.pageTitle;
}

function handleSelectEntry(entry: ScratchpadEntryApiModel): void {
  currentChatEntry.value = entry;
}

function addNewChatToHistory(): void {
  if (currentChatEntry.value) {
    if (chatHistory.value.length === 0 || chatHistory.value[0].text === "") {
      chatHistory.value[0] = currentChatEntry.value;
    } else {
      chatHistory.value.unshift(currentChatEntry.value);
    }
  }
}

function createNewPage(): void {
  const subjectQuery = route.query.subject as string;
  const idQuery = route.query.id as string;
  const authorId = csContext.user.id;
  currentChatEntry.value = createNewChatEntry(authorId, subjectQuery, idQuery);
  addNewChatToHistory();
}

function handleNewPageClick(): void {
  createNewPage();
}

function updateHistoryItem(entry: ScratchpadEntryApiModel): void {
  const index = chatHistory.value.findIndex((item) => item.pageId === entry.pageId);

  if (index !== -1) {
    chatHistory.value.splice(index, 1, entry);
  }
}

async function deleteEntry(entry: ScratchpadEntryApiModel): Promise<void> {
  const response = await deleteChatEntry(entry.authorId, entry.pageId);
  if (response.error) {
    deleteError.value = response.error;
  }

  const index = chatHistory.value.findIndex((item) => item.pageId === entry.pageId);

  if (index !== -1) {
    chatHistory.value.splice(index, 1);
  }
  createNewPage();
}

onMounted(async () => {
  chatHistory.value = await getAllScratchpadEntries();
  createNewPage();
});
</script>

<style scoped lang="less">
@import (reference) "../styles/bootstrap/variables.less";

.scratchpad-container {
  margin: 2rem;
}

.history-scroll-area {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: scroll;

  @media (max-width: @screen-sm-max) {
    flex-direction: row;
    flex: 1;
    height: 50px;
    align-items: center;
  }

  &::-webkit-scrollbar {
    display: none;
    @media (max-width: @screen-sm-max) {
      display: block;
      height: 5px;
    }
  }
}

.flex-gap-2 {
  display: flex;
  gap: 2rem;

  @media (max-width: @screen-sm-max) {
    flex-direction: column;
  }
}

.history-list {
  list-style-type: none;
  padding: 0;
  @media (max-width: @screen-sm-max) {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0;
  }
}

.history-list-item {
  margin-bottom: 0.5rem;
  cursor: pointer;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  width: 220px;

  &:hover {
    background-color: @registration-background-color;
  }
  @media (max-width: @screen-sm-max) {
    width: 120px;
  }

  .history-list-item-link {
    padding: 1rem;
    display: block;
    @media (max-width: @screen-sm-max) {
      padding: 0.5rem;
    }
  }
}

.selected-chat {
  background-color: @registration-background-color;
}

.flex-item {
  flex: 1;
}

.glyphicon-trash:hover {
  color: @brand-primary;
}
</style>
